import { Component, inject } from '@angular/core';
import { CardListConfig } from '../../shared/dashboard/card-list/card-list-config';
import { Attributes } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DataOptions, DataResponse, DataService, ExportFilter, FilterExpression } from '../../services/data.service';
import { lastValueFrom, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { DataRow, ExportColumnDef, getGroupAttributes, getInitialAttributes } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { AttrPath, IndicatorNaamPrestatieanalyse, Table, VsoCluster } from '@cumlaude/metadata';
import { getLeafA, getPathKey, Level, Path } from '../../services/data-tree';
import { DashboardContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { CardPaCellComponent, PrestatieanalyseCellData } from './card-pa-cell/card-pa-cell.component';
import { formatNumber, formatPercent } from '@angular/common';
import { concat, dropWhile, flatMap, isFunction, isUndefined, last, memoize, omitBy, reverse, tail, zip, zipWith } from 'lodash-es';
import { take } from 'rxjs/operators';
import { CardPaHistoryCellComponent, PrestatieanalyseHistoryCellData } from './card-pa-history-cell/card-pa-history-cell.component';
import { PointInfo } from '../../shared/dashboard/linechart-table/linechart/linechart.component';
import { MultiAggregator, noAgg0 } from '../../services/aggregation';
import { lookup } from '../../services/tabulate';
import { ExportOptions, ExportType } from '../../services/export.service';
import { getSchooljaarKort } from '@cumlaude/shared-utils';
import { createYAxis } from '../../services/axis';
import { DashboardVariant } from '../../services/weergave-opties';
import { TooltipElement } from '@cumlaude/shared-components-overlays';
import { CardListComponent } from '../../shared/dashboard/card-list/card-list.component';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { DisplayService } from '../../services/display.service';
import { FilterName } from '../../services/filter-name';
import { LegendaComponent } from '../../legenda/legenda.component';
import { ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';

export interface PrestatieanalyseI extends Attributes {
	pv_nm_indicator: IndicatorNaamPrestatieanalyse;
	'pv_fkd_vcv.vcv_nm_cluster': VsoCluster | null;
	pv_nm_schooljaar?: string;
	pv_m_nr_score: number;
	ppv_m_nr_p25: number;
	ppv_m_nr_p50: number;
	ppv_m_nr_p75: number;
}

export interface PrestatieanalyseA extends Attributes {
	minScoreNorm: number;
	maxScoreNorm: number;
	score: number;
}

@Component({
	selector: 'app-prestatieanalyse',
	templateUrl: './prestatieanalyse.component.html',
	styleUrls: ['./prestatieanalyse.component.scss'],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		CardListComponent,
		CardPaCellComponent,
		CardPaHistoryCellComponent,
		FilterBarComponent,
		LegendaComponent,
		ToggleButtonsComponent,
	],
})
export class PrestatieanalyseComponent extends CardListConfig<PrestatieanalyseI, Attributes, PrestatieanalyseA> {
	protected readonly dataService = inject(DataService);
	protected readonly displayService = inject(DisplayService);
	protected readonly router = inject(Router);

	factTable = Table.fac_pv_prestatieanalyse_vso;

	protected exportTypes = [ExportType.AFBEELDING, ExportType.PDF, ExportType.TABEL];

	variant = this.qp.signal('variant');

	actueelFilters: FilterName[] = ['pv_nm_schooljaar', 'pv_fk_vs.vs_nm_vestiging'];

	historieFilters: FilterName[] = ['x_pv_schooljaar_historie', 'x_pv_multiselect_schooljaar', 'pv_fk_vs.vs_nm_vestiging'];

	permanentFilterExpressions: FilterExpression[] = [];

	groups: AttrPath[] = [['pv_fk_vs', 'vs_nm_vestiging']];

	actueelSubgroups: AttrPath[] = [['pv_nm_indicator'], ['pv_fkd_vcv', 'vcv_nm_cluster']];

	historieSubgroups: AttrPath[] = [['pv_nm_indicator'], ['pv_nm_schooljaar'], ['pv_fkd_vcv', 'vcv_nm_cluster']];

	protected override singleAggregators = {
		score: noAgg0('pv_m_nr_score'),
	};

	protected override multiAggregators: MultiAggregator<keyof PrestatieanalyseA, PrestatieanalyseI, PrestatieanalyseA, number>[] = [
		{
			attribute: 'minScoreNorm',
			init: ({ pv_m_nr_score, ppv_m_nr_p25 }) => Math.min(pv_m_nr_score ?? Infinity, ppv_m_nr_p25 ?? Infinity),
			combine: (as) => Math.min(...as.map((a) => a.minScoreNorm)),
		},
		{
			attribute: 'maxScoreNorm',
			init: ({ pv_m_nr_score, ppv_m_nr_p75 }) => Math.max(pv_m_nr_score ?? -Infinity, ppv_m_nr_p75 ?? -Infinity),
			combine: (as) => Math.max(...as.map((a) => a.maxScoreNorm)),
		},
	];

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.dataService.getPrestatieanalyseVsoData(options);
	}

	getRecords(
		path: Path<PrestatieanalyseA, number[]>,
		context: DashboardContext<PrestatieanalyseI, PrestatieanalyseA, PrestatieanalyseComponent>,
		variant: DashboardVariant
	): Path<PrestatieanalyseA, number[]>[] {
		if (variant === DashboardVariant.ACTUEEL) {
			return joinRecords(path, <Level<PrestatieanalyseA, number[]>>context.orderRoot);
		}

		const vestigingLvl = last(path)!;
		return vestigingLvl.c.map((indicatorLvl: Level<PrestatieanalyseA, number[]>) => [...path, indicatorLvl]);
	}

	makeActueelCell(
		path: Path<PrestatieanalyseA, number[]>,
		context: DashboardContext<PrestatieanalyseI, PrestatieanalyseA, PrestatieanalyseComponent>
	): PrestatieanalyseCellData {
		const { subgroupNames, measureNames } = context;
		const attrs = getInitialAttributes<PrestatieanalyseI>(subgroupNames, measureNames, path);
		const { ppv_m_nr_p25, ppv_m_nr_p75 } = attrs;
		const tooltipItems = this.getTooltipItems(attrs, false);
		const is_onvoldoende = isOnvoldoende(attrs);
		const p25_p75: [number, number] | null = ppv_m_nr_p25 === null || ppv_m_nr_p75 === null ? null : [ppv_m_nr_p25, ppv_m_nr_p75];
		return { ...attrs, p25_p75, tooltip: tooltipItems, is_onvoldoende };
	}

	getTooltipItems(attrs: PrestatieanalyseI, reversed: boolean): TooltipElement[] {
		const { pv_m_nr_score, ppv_m_nr_p25, ppv_m_nr_p50, ppv_m_nr_p75 } = attrs;
		const formatter =
			attrs['pv_nm_indicator'] === IndicatorNaamPrestatieanalyse.BEOOGD_I_R_T_BEHAALD_EINDUITSTROOMNIVEAU
				? (x: number) => formatNumber(x, 'nl_NL', '1.2-2')
				: (x: number) => formatPercent(x / 100, 'nl_NL', '1.2-2');
		const [score_fmt, p25_fmt, p50_fmt, p75_fmt] = [pv_m_nr_score, ppv_m_nr_p25, ppv_m_nr_p50, ppv_m_nr_p75].map((x) =>
			x === null ? 'Onb' : formatter(x)
		);
		const tooltipItems: TooltipElement[] = [{ label: 'Score', value: score_fmt, bold: false }];
		tooltipItems.push({
			label: 'Cluster',
			value: this.displayService.display(attrs['pv_fkd_vcv.vcv_nm_cluster'], ['pv_fkd_vcv', 'vcv_nm_cluster']),
			bold: false,
		});

		function scoreBetween(lo: number, hi: number) {
			if (pv_m_nr_score === null) return false;
			return pv_m_nr_score >= lo && pv_m_nr_score < hi;
		}

		if (ppv_m_nr_p25 !== null) {
			const percentielItems: TooltipElement[] = [
				{ label: 'Percentiel 0-25', value: `... - ${p25_fmt}`, bold: scoreBetween(-Infinity, ppv_m_nr_p25) },
				{ label: 'Percentiel 25-50', value: `${p25_fmt} - ${p50_fmt}`, bold: scoreBetween(ppv_m_nr_p25, ppv_m_nr_p50) },
				{ label: 'Percentiel 50-75', value: `${p50_fmt} - ${p75_fmt}`, bold: scoreBetween(ppv_m_nr_p50, ppv_m_nr_p75) },
				{ label: 'Percentiel 75-100', value: `${p75_fmt} - ...`, bold: scoreBetween(ppv_m_nr_p75, Infinity) },
			];

			if (reversed) reverse(percentielItems);

			tooltipItems.push(...percentielItems);
		}

		return tooltipItems;
	}

	makeHistorieCell = memoize(
		(p: Path<PrestatieanalyseA, number[]>) =>
			memoize((c: DashboardContext<PrestatieanalyseI, PrestatieanalyseA, PrestatieanalyseComponent>) =>
				this._makeHistorieCell.bind(this)(p, c)
			),
		(p) => getPathKey(p)
	);

	_makeHistorieCell(
		path: Path<PrestatieanalyseA, number[]>,
		context: DashboardContext<PrestatieanalyseI, PrestatieanalyseA, PrestatieanalyseComponent>
	): PrestatieanalyseHistoryCellData {
		const { subgroupNames, measureNames } = context;
		const pv_nm_indicator = <IndicatorNaamPrestatieanalyse>last(path)!.k;
		const scoreData: { label: string; data: PointInfo[]; tooltip: TooltipElement[] }[] = [];
		const areas: PointInfo[][] = [];

		const recs = joinRecords(
			path,
			(<Level<PrestatieanalyseA, number[]>>context.orderRoot).c.find((indicatorLvl) => indicatorLvl.k === last(path)!.k)!
		);
		const lastRecIx = recs.length - 1;
		let is_onvoldoende = false;

		recs.forEach((rec, recIx) => {
			const attrs = getInitialAttributes<PrestatieanalyseI>(subgroupNames, measureNames, rec);
			const { pv_nm_schooljaar, pv_m_nr_score, ppv_m_nr_p25, ppv_m_nr_p50, ppv_m_nr_p75 } = attrs;
			const data = pv_m_nr_score === null ? [] : [{ lineClass: 'pv_nr_score', qty: pv_m_nr_score }];
			const label = getSchooljaarKort(pv_nm_schooljaar!);
			const tooltip = pv_m_nr_score === null ? [] : this.getTooltipItems(attrs, true);
			scoreData.push({ label, data, tooltip });
			const percentielData =
				ppv_m_nr_p25 === null || ppv_m_nr_p75 === null || ppv_m_nr_p50 === null
					? []
					: [
							{ lineClass: 'p25-area', qty: ppv_m_nr_p25 },
							{ lineClass: 'p50-area', qty: ppv_m_nr_p50 },
							{ lineClass: 'p75-area', qty: ppv_m_nr_p75 },
						];
			areas.push(percentielData);
			if (recIx === lastRecIx) is_onvoldoende = isOnvoldoende(attrs);
		});
		const minMax = getMinMax(pv_nm_indicator, path);
		const yAxis = createYAxis(minMax);
		const linechartProps = {
			data: [{ label: '', data: scoreData }],
			areas,
			yAxis,
			lineNames: {},
		};
		return { pv_nm_indicator, is_onvoldoende, linechartProps };
	}

	onClick(path: Path<PrestatieanalyseA, number[]>, context: DashboardContext<PrestatieanalyseI, PrestatieanalyseA, PrestatieanalyseComponent>) {
		// gebruik het path op het diepste niveau, anders werkt getInitialAttributes niet goed
		const deepPath = last(path)!.r[0];
		const nm_vestiging = getGroupAttributes(context, deepPath)['pv_fk_vs.vs_nm_vestiging'];
		const { subgroupNames, measureNames } = context;
		const { pv_nm_indicator } = getInitialAttributes<PrestatieanalyseI>(subgroupNames, measureNames, deepPath);
		const dashboard = this.getDashboard(pv_nm_indicator);
		lastValueFrom(this.filterService.getFilterState('pv_nm_schooljaar').pipe(take(1)))
			.catch((err) => (err.name == 'EmptyError' ? Promise.resolve(undefined) : Promise.reject(err)))
			.then((schooljaar) => {
				const params = {};

				if (nm_vestiging) Object.assign(params, this.filterService.encodeFilterValue('ds_fk_vs_van.vs_nm_vestiging', nm_vestiging));

				if (schooljaar) Object.assign(params, this.filterService.encodeFilterValue('x_prestatieanalyse_ds_schooljaar', schooljaar));

				this.urlService.redirect([dashboard], params);
			});
	}

	trackByPathKey(_index: number, path: Path<PrestatieanalyseA, number[]>) {
		return getPathKey(path);
	}

	private getDashboard(indicator: IndicatorNaamPrestatieanalyse): string {
		switch (indicator) {
			case IndicatorNaamPrestatieanalyse.BEOOGD_I_R_T_BEHAALD_EINDUITSTROOMNIVEAU:
				return 'prestatieanalyse/beoogd-irt-behaald';
			case IndicatorNaamPrestatieanalyse.BEHAALD_NIVEAU_CONFORM_OPP:
				return 'prestatieanalyse/behaald-niveau-conform-opp';
			case IndicatorNaamPrestatieanalyse.EINDUITSTROOM:
				return 'prestatieanalyse/einduitstroom';
			case IndicatorNaamPrestatieanalyse.TUSSENTIJDSE_UITSTROOM:
				return 'prestatieanalyse/tussentijdse-uitstroom';
		}
	}

	override getExportTable(
		context: DashboardContext<PrestatieanalyseI, PrestatieanalyseA, PrestatieanalyseComponent>,
		exportOptions: ExportOptions,
		title: string,
		filters: ExportFilter[]
	) {
		const type = (row: DataRow<PrestatieanalyseA>) => {
			const indicator = row._path[2].k;
			if (indicator === IndicatorNaamPrestatieanalyse.BEOOGD_I_R_T_BEHAALD_EINDUITSTROOMNIVEAU) return 'number';
			else return 'percentage';
		};

		const format = (row: DataRow<PrestatieanalyseA>) => {
			const indicator = row._path[2].k;
			if (indicator === IndicatorNaamPrestatieanalyse.BEOOGD_I_R_T_BEHAALD_EINDUITSTROOMNIVEAU) return '1.2-2';
			else return '1.0-0';
		};

		const { subgroupNames, measureNames } = context;

		// Het path dat getValue meekrijgt, bevat [root, vestiging, indicator, (schooljaar indien historie), cluster]
		const columns = concat<ExportColumnDef<PrestatieanalyseA> & { getValue: (path: Path<PrestatieanalyseA, number[]>) => any }>(
			[{ name: 'Vestiging', type: 'string', getValue: (path) => path[1].k }],
			this.variant() === DashboardVariant.HISTORIE ? [{ name: 'Schooljaar', type: 'string', getValue: (path) => path[3].k }] : [],
			[
				{ name: 'Indicator', type: 'string', getValue: (path) => path[2].k },
				{ name: 'Cluster', type: 'string', getValue: (path) => last(path)!.k },
				{ name: 'Score', type, format, getValue: (path) => getLeafA(path).score },
				{
					name: 'Percentiel 25',
					type,
					format,
					getValue: (path) => getInitialAttributes<PrestatieanalyseI>(subgroupNames, measureNames, path).ppv_m_nr_p25,
				},
				{
					name: 'Percentiel 50',
					type,
					format,
					getValue: (path) => getInitialAttributes<PrestatieanalyseI>(subgroupNames, measureNames, path).ppv_m_nr_p50,
				},
				{
					name: 'Percentiel 75',
					type,
					format,
					getValue: (path) => getInitialAttributes<PrestatieanalyseI>(subgroupNames, measureNames, path).ppv_m_nr_p75,
				},
			]
		);
		const rows = flatMap(
			context.dataRoot!.r.map((path) =>
				last(path)!.xr!.map((path) =>
					columns.map((col, ix) => {
						const format = isFunction(col.format) ? col.format({ _id: ix.toString(), _path: path }) : undefined;
						const type = isFunction(col.type) ? col.type({ _id: ix.toString(), _path: path }) : undefined;

						let value = col.getValue(path);
						if (type === 'percentage') value = value / 100;

						if (format || type) {
							return omitBy({ value, format, type }, isUndefined);
						}
						return value;
					})
				)
			)
		);
		return this.buildSinglePageExportTable(exportOptions, title, filters, [{ columns, rows }]);
	}

	protected readonly DashboardVariant = DashboardVariant;
}

function getMinMax(pv_nm_indicator: IndicatorNaamPrestatieanalyse, path: Path<PrestatieanalyseA, number[]>): [number, number] {
	if (pv_nm_indicator === IndicatorNaamPrestatieanalyse.BEOOGD_I_R_T_BEHAALD_EINDUITSTROOMNIVEAU) {
		const max = Math.max(6, last(path)!.a.maxScoreNorm);
		return [0, max];
	}

	return [0, 100];
}

/**
 * Voegt de measures samen voor een record uit de data-query (pv_nr_score) en de order-query (ppv_nr_p25 etc) met een soort COALESCE.
 */
function mergeInto(target: number[], source: number[]) {
	const res = zipWith(target, source, (t, s) => t ?? s);
	target.splice(0, target.length, ...res);
}

/**
 * Joint de dim_ppv records in orderRoot met de fac_pv records in dataPath op nm_indicator en nm_cluster, en voegt daarbij de measures samen.
 * Het is een variant op een outer join: als er geen record in dataPath is voor welk nm_cluster dan ook, wordt
 * het dim_ppv record met nm_cluster NULL genomen en daar een NULL score aan gehangen.
 */
function joinRecords(dataPath: Path<PrestatieanalyseA, number[]>, orderRoot: Level<PrestatieanalyseA, number[]>) {
	const dataLvl = last(dataPath)!;
	const newRecords = flatMap(orderRoot.r, (orderPath) => {
		const orderPathEnd = tail(dropWhile(orderPath, (lvl) => lvl.k !== orderRoot.k));
		const found = lookup(dataLvl, orderPathEnd);
		if (found) {
			mergeInto(found.v as number[], last(orderPathEnd)!.v as number[]);
			return [found.r[0]];
		} else if (last(orderPath)!.k === null) {
			return [fixRecord(dataPath, orderPathEnd)];
		} else {
			return [];
		}
	});
	return dedupClusters(newRecords);
}

/**
 * Houdt van twee opeenvolgende records die alleen op nm_cluster verschillen (in de praktijk is de eerste dan een gelukte join van score-data met
 * percentieldata, en de tweede een NULL score met NULL percentieldata) alleen de eerste over.
 */
function dedupClusters(records: Path<PrestatieanalyseA, number[]>[]) {
	const pairs = <[Path<PrestatieanalyseA, number[] | undefined>, Path<PrestatieanalyseA, number[]>][]>(
		zip([undefined, ...records.slice(0, -1)], records)
	);
	return flatMap(pairs, ([path1, path2]) =>
		path1
			?.slice(0, -1)
			.map((lvl) => lvl.k)
			.join(';') ===
		path2
			.slice(0, -1)
			.map((lvl) => lvl.k)
			.join(';')
			? []
			: [path2]
	);
}

/**
 * Maak een nieuw Path uit dataPath + orderPathEnd.
 * Eigenlijk zouden we ook de attributen van alle Levels van dit nieuwe Path moeten updaten maar dat doen we niet.
 * Het r-attribuut van het laaste Level wordt wel gebruikt (in onClick) dus die passen we aan.
 */
function fixRecord<A, D>(dataPath: Path<A, D>, orderPathEnd: Path<A, D>): Path<A, D> {
	const newRecord = [...dataPath, ...orderPathEnd];
	newRecord[newRecord.length - 1] = { ...newRecord.at(-1)!, r: [newRecord] };
	return newRecord;
}

function isOnvoldoende(attrs: PrestatieanalyseI) {
	const { pv_m_nr_score, ppv_m_nr_p25 } = attrs;
	return pv_m_nr_score !== null && ppv_m_nr_p25 !== null && pv_m_nr_score < ppv_m_nr_p25;
}
